/**
 * The words of Dashboard › Hiring's Notice period tab (16 Oct 2026): the stores where someone
 * deployed is serving notice, so hiring to replace them can be raised from there rather than
 * noted down from the Deployment dashboard and raised from the Stores page.
 *
 * Short on purpose, as the rest of the dashboard: a label, a date and a word, and anything
 * longer on hover. Every word the tab prints is here, with the small rules that pick it, so a
 * test can hold the words without drawing a page.
 *
 * The people named are employees, by name and code - never a candidate.
 */
import type { HiringNoticeList, HiringNoticeRow } from '../../services/api';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const INDIA = 'Asia/Kolkata';
const dayKey = (d: Date): string => d.toLocaleDateString('en-CA', { timeZone: INDIA });

const people = (n: number): string => (n === 1 ? '1 person' : `${n} people`);

/** "Sample Store 01 (SS01)", as the dashboard writes a store. */
export const noticeStoreWords = (row: Pick<HiringNoticeRow, 'store'>): string => {
  const name = (row.store.storeName ?? '').trim();
  const code = (row.store.storeCode ?? '').trim();
  if (name && code) return `${name} (${code})`;
  return name || code || 'A store';
};

/**
 * The Store cell's two parts: the name, which may be cut, and "(SS01)", which never is - the
 * code is what tells two stores of similar names apart. `code` is null when the store has no
 * name or no code: then `name` is the whole of noticeStoreWords.
 */
export const noticeStoreParts = (row: Pick<HiringNoticeRow, 'store'>): { name: string; code: string | null } => {
  const name = (row.store.storeName ?? '').trim();
  const code = (row.store.storeCode ?? '').trim();
  return name && code ? { name, code: `(${code})` } : { name: noticeStoreWords(row), code: null };
};

/** The person's name, or their code when ChannelPlex and 1Office hold no name. */
export const noticePersonName = (row: Pick<HiringNoticeRow, 'person'>): string =>
  (row.person.name ?? '').trim() || row.person.empCode;

/**
 * Under the name: "910005 · Sample Promoter" (the code, then the designation when there is
 * one). When there is no name the line above is already the code, so this is the designation
 * alone (or nothing): the code is never said twice.
 */
export const noticePersonLine = (row: Pick<HiringNoticeRow, 'person'>): string => {
  const named = !!(row.person.name ?? '').trim();
  return [named ? row.person.empCode : '', (row.person.designation ?? '').trim()].filter(Boolean).join(' · ');
};

/**
 * A last working day as the dashboard writes a day: "Today", "Tomorrow", "31 Oct", or "5 Jan
 * 2027" in another year. India time. Null when there is no date.
 */
export const noticeDayWords = (day: string | null | undefined, now: Date = new Date()): string | null => {
  const m = typeof day === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(day) : null;
  if (!m) return null;
  const today = dayKey(now);
  const tomorrow = dayKey(new Date(now.getTime() + 24 * 60 * 60 * 1000));
  if (day === today) return 'Today';
  if (day === tomorrow) return 'Tomorrow';
  const short = `${Number(m[3])} ${MONTHS[Number(m[2]) - 1] ?? m[2]}`;
  return m[1] === today.slice(0, 4) ? short : `${short} ${m[1]}`;
};

export const NOTICE_WORDS = {
  /** The tab in the dashboard's strip, after the four statuses: "Notice period (5)". */
  tab: 'Notice period',
  /**
   * The tab's label in the strip, which shows from every tab - so its count is never one that
   * is not known. "Notice period (5)" when 1Office was read in full; "Notice period (5+)" when
   * some people could not be checked (at least five; more may be serving notice); no count at
   * all when 1Office could not be read or the person has no 1Office account, or before the
   * first answer.
   */
  tabLabel: (list: Pick<HiringNoticeList, 'oneOffice' | 'page'> | null | undefined): string => {
    const total = list?.page?.total;
    const read = list?.oneOffice?.read;
    if (typeof total !== 'number' || !Number.isFinite(total) || !read || read === 'unavailable' || read === 'no_identity') return 'Notice period';
    return read === 'partial' ? `Notice period (${total}+)` : `Notice period (${total})`;
  },
  /** On hover of the tab, when its count is not the whole of it: why. */
  tabHover: (list: Pick<HiringNoticeList, 'oneOffice'> | null | undefined): string | undefined => {
    const read = list?.oneOffice?.read;
    if (read === 'partial') return 'Some people could not be checked in 1Office just now, so there may be more.';
    if (read === 'unavailable') return '1Office could not be read just now.';
    if (read === 'no_identity') return 'You have no 1Office account, so ChannelPlex cannot read who is serving notice for you.';
    return undefined;
  },
  searchLabel: 'Search store or person',
  searchPlaceholder: 'Search store or person',
  columns: { store: 'Store', person: 'Person', lastDay: 'Last working day', hiring: 'Hiring' },
  /** The Hiring column: whether hiring already replaces the person. */
  raised: 'Raised',
  notRaised: 'Not raised',
  raisedHover: (row: Pick<HiringNoticeRow, 'hiring'>): string | undefined => {
    if (!row.hiring.raised) return undefined;
    if (row.hiring.by === 'opening' && row.hiring.openingId != null) return `Opening ${row.hiring.openingId} replaces them.`;
    if (row.hiring.by === 'being_raised') return 'Hiring to replace them is being raised.';
    if (row.hiring.by === 'request') return 'A hiring request to replace them is waiting for confirmation.';
    return 'Hiring already replaces them.';
  },
  /** No last working day yet, in grey; why, on hover. */
  noDate: 'Not set yet',
  noDateHover: (row: Pick<HiringNoticeRow, 'state' | 'resignedOn'>, now: Date = new Date()): string => {
    if (row.state === 'resigned') return 'Resigned in 1Office. HR has not started the exit yet, so there is no last working day.';
    const on = noticeDayWords(row.resignedOn, now);
    return on ? `Resigned ${on === 'Today' ? 'today' : `on ${on}`}. 1Office has no last working day yet.` : '1Office has no last working day yet.';
  },
  /** The row's controls, as a screen reader names them. */
  raiseFor: (row: HiringNoticeRow): string => `Raise hiring to replace ${noticePersonName(row)} at ${noticeStoreWords(row)}`,
  askFor: (row: HiringNoticeRow): string => `Ask for hiring to replace ${noticePersonName(row)} at ${noticeStoreWords(row)}`,
  linkFor: (row: HiringNoticeRow): string => `Link an opening for ${noticeStoreWords(row)}`,
  openStoreFor: (row: HiringNoticeRow): string => `Open hiring for ${noticeStoreWords(row)}`,
  openRequestFor: (row: HiringNoticeRow): string => `Open hiring request for ${noticePersonName(row)}`,
  /** On hover of a disabled Ask: the store already has a request waiting. */
  askWaiting: 'A hiring request for this store is already waiting for confirmation',
  none: 'Nobody at your stores is serving notice.',
  noneFound: 'Nothing matches.',
  loadFailed: 'Could not load who is serving notice.',
  storeFailed: 'Could not open this store’s hiring.',
  /**
   * The one plain line above the table when 1Office was not read in full: never a silent
   * empty list. Null when it was.
   */
  readLine: (oneOffice: HiringNoticeList['oneOffice'] | null | undefined): string | null => {
    if (!oneOffice) return null;
    if (oneOffice.read === 'unavailable') return '1Office could not be read just now, so nobody serving notice can be shown. Try again in a moment.';
    if (oneOffice.read === 'no_identity') return 'You have no 1Office account, so ChannelPlex cannot read who is serving notice for you.';
    if (oneOffice.read === 'partial' && oneOffice.notChecked > 0) {
      return `1Office could not be read for ${people(oneOffice.notChecked)} just now, so someone serving notice may be missing. Everyone else is shown.`;
    }
    if (oneOffice.read === 'partial') return '1Office could not be read in full just now, so someone serving notice may be missing. Everyone else is shown.';
    return null;
  },
} as const;
